import { FormEvent, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/features/auth/session-provider';
import { friendlyAuthError } from '@/features/auth/friendly-error';
import { InlineAlert } from '@/components/inline-alert';
import { CaremoryMark } from '@/components/icons/caremory-mark';

export function LoginPage() {
  const { session, loading } = useSession();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/home';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!loading && session) {
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: authError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });
    setSubmitting(false);
    if (authError) {
      console.error('[login] signIn failed', authError);
      setError(friendlyAuthError(authError));
    }
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-4 safe-top safe-bottom bg-gradient-to-b from-accent-soft/60 via-canvas to-canvas anim-fade-in">
      <div className="flex flex-col items-center mb-6">
        <CaremoryMark variant="primary" className="h-10 mb-3" title="Caremory" />
        <p className="text-sm font-semibold text-ink-muted text-center max-w-xs tracking-wide">
          Care that remembers
        </p>
      </div>
      <form onSubmit={handleSubmit} className="card p-6 w-full max-w-sm">
        <h2 className="text-lg font-bold mb-1">Sign in</h2>
        <p className="text-sm text-ink-muted mb-6">
          Welcome back.
        </p>

        <label className="label" htmlFor="email">
          Email
        </label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="input mb-4"
        />

        <div className="flex items-baseline justify-between">
          <label className="label" htmlFor="password">
            Password
          </label>
          <button
            type="button"
            onClick={() =>
              alert(
                'Password reset coming soon.',
              )
            }
            className="text-xs text-accent hover:text-accent-hover font-medium"
          >
            Forgot?
          </button>
        </div>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          required
          minLength={6}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="input mb-4"
        />

        {error && <InlineAlert className="mb-4">{error}</InlineAlert>}

        <button
          type="submit"
          disabled={submitting}
          className="btn-primary w-full mb-3"
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="text-sm text-ink-muted text-center">
          New here?{' '}
          <Link to="/signup" className="text-accent hover:text-accent-hover">
            Create an account
          </Link>
        </p>
      </form>
    </main>
  );
}
